// Web Audio chime for scanner saved-view alerts. No asset to host — a short
// two-tone "ding-dong" synthesized via an AudioContext. Browsers block audio
// until a user gesture has occurred on the page, so unlockAudio() is called
// from the alert-toggle's onClick (a real gesture). After that, playChime()
// can fire programmatically from the polling watcher.

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch {
      return null;
    }
  }
  return ctx;
}

/** Call from a user gesture (the alert toggle click) so the browser permits
 *  later programmatic playback. Resumes a suspended context. Safe to call
 *  repeatedly. */
export function unlockAudio(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') void c.resume();
}

/** AudioContext time at which the current ring finishes — used to ignore a
 *  second playChime() while one is still sounding, so simultaneous alerts
 *  don't stack into noise. */
let ringUntil = 0;

function tone(c: AudioContext, freq: number, start: number, dur: number, peak = 0.16): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  // Quick attack, smooth exponential decay — a pleasant chime, not a buzz.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(start);
  osc.stop(start + dur);
}

// A bright A-major arpeggio (A5 · C#6 · E6) played as a quick rising motif and
// repeated every CYCLE_S for ~3 seconds — clearly reads as "alert" without
// being a harsh alarm.
const ARPEGGIO = [880, 1108.73, 1318.51];
const CYCLE_S = 0.7;
const CYCLES = 4; // ~2.8s of motifs; the tail rings out near 3.1s

/** Play the repeating alert chime (~3 s): a rising A-major arpeggio repeated
 *  four times. No-op if Web Audio is unavailable or the context is still locked
 *  (no prior gesture) — the visual notification/toast carries the alert
 *  regardless. A second call while a ring is already sounding is ignored so
 *  simultaneous alerts don't overlap into noise. */
export function playChime(): void {
  const c = getCtx();
  if (!c) return;
  // Best-effort resume; if it's still suspended the scheduled tones simply
  // won't sound, which is the acceptable degraded path.
  if (c.state === 'suspended') void c.resume();
  try {
    const t = c.currentTime;
    if (t < ringUntil) return; // a ring is already in progress
    for (let i = 0; i < CYCLES; i++) {
      const start = t + i * CYCLE_S;
      ARPEGGIO.forEach((f, j) => tone(c, f, start + j * 0.1, 0.24, 0.15));
    }
    ringUntil = t + CYCLES * CYCLE_S;
  } catch {
    /* ignore — never let an audio hiccup break the watcher */
  }
}
